import { graphql, rest } from 'msw';

export const buildTokenInfo = (token: string) => (bank: string) => (
  status: string
) => ({
  token: token,
  amount: '20.7',
  memo: 'test again with local bank account service',
  status: status,
  expiresAt: '2020-11-13T20:29:34.000Z',
  publicId: Math.random().toString(36),
  receiverIdentifier: 'hatsam1@gmail.com',
  claimWithChime: true,
  sender: {
    id: 938,
    bank,
    name: 'Hat Sam',
    referralToken: 'hatsam1',
    referralFinePrint: null,
  },
  recipientPreviouslyClaimed: false,
  flipperFlags: {
    ocrEnabled: true,
    graphqlUsage: {
      // NOTE: useGqlGetAndClaimPayAnyone flag is no longer used - GraphQL is always used now
      useGqlGetAndClaimPayAnyone: true,
    },
  },
  maintenanceMode: false,
});

let numberOfCalls = 0;
let isFormSubmitted = false;

// Centralized error mapping function
const getPayAnyoneClaimError = (value: string) => {
  switch (value) {
    case '11111':
    case '99999':
      return {
        __typename: 'PayAnyoneClaimError',
        error_id: 'claimed_from_expired_status',
        error_header: 'Dang! Payment expired.',
        error_body:
          "This payment is no longer available. Ask your friend to send money again.\nYou can still join Chime, and you'll both get $50 when you set up a qualifying direct deposit of $200+ within 45 days of enrolling.",
      };
    case '22222':
      return {
        __typename: 'PayAnyoneClaimError',
        error_id: 'generic',
        error_header: 'Something went wrong',
        error_body:
          "We're working on it. Please try again later or contact our Member Services team if you need more support.",
      };
    case '33333':
      return {
        __typename: 'PayAnyoneClaimError',
        error_id: 'claimed_from_succeeded_status',
        error_header: 'Payment already claimed',
        error_body:
          "This payment has already been claimed. If this wasn't you, please contact us immediately.",
      };
    case '44444':
      return {
        __typename: 'PayAnyoneClaimError',
        error_id: 'claimed_from_nonpending_status',
        error_header: 'Something went wrong',
        error_body:
          "We're working on it. Please try again later or contact our Member Services team if you need more support.",
      };
    case '55555':
      return {
        __typename: 'PayAnyoneClaimError',
        error_id: 'exceeded_limits',
        error_header: 'Limit reached',
        error_body:
          'You can only claim up to $1,000 per month. Please contact our Member Services team if you need more support.',
      };
    case '66666':
      return {
        __typename: 'PayAnyoneClaimError',
        error_id: 'debit_card_failed_validation',
        error_header:
          "We couldn't verify your debit card. Please check the information and try again.",
        error_body: '',
      };
    case '77777':
      return {
        __typename: 'PayAnyoneClaimError',
        error_id: 'debit_card_not_eligible',
        error_header:
          "We don't accept prepaid, credit, international, or Chime cards. Please try again with a U.S. debit card.",
        error_body: '',
      };
    case '88888':
      return {
        __typename: 'PayAnyoneClaimError',
        error_id: 'receiver_blacklisted',
        error_header: "We're unable to process this request",
        error_body:
          'Please contact our Member Services team if you need more support.',
      };
    case '99991':
      throw new Error('Internal Server Error');
    default:
      return null;
  }
};

// Polling status mapping
const pollingStatusMap = {
  '1': { status: 'sent', bank: 'stride' },
  '2': { status: 'pending', bank: 'bancorp' },
  '3': { status: 'expired', bank: 'bancorp' },
  '4': { status: 'succeeded', bank: 'bancorp' },
  '5': { status: 'cancelled', bank: 'bancorp' },
  '6': { status: 'sent', bank: 'bancorp' },
  '7': { status: 'failed', bank: 'bancorp' },
  '8': { status: 'reversed', bank: 'bancorp' },
  '9': { status: 'succeeded', bank: 'stride' },
  '10': { status: 'failed', bank: 'stride' },
  '11': { status: 'reversed', bank: 'stride' },
  '12': { status: 'succeeded', bank: 'stride' },
  '13': { status: 'sent', bank: 'stride' },
};

export const handlers = [
  // Card encryption keys endpoint
  graphql.query('P2pCardEncryptionKeys', (req, res, ctx) => {
    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            card_encryption_keys: [
              {
                id: '1234',
                type: 'tabapay',
                name: 'tabapay:stride',
                key:
                  typeof window !== 'undefined'
                    ? window.atob(process.env.TABAPAY_PUBLIC_KEY_EXAMPLE)
                    : 'mock-key-server-side',
                __typename: 'P2pCardEncryptionKey',
              },
              {
                id: '5678',
                type: 'tabapay',
                name: 'tabapay:bancorp',
                key:
                  typeof window !== 'undefined'
                    ? window.atob(process.env.TABAPAY_PUBLIC_KEY_EXAMPLE)
                    : 'mock-key-server-side',
                __typename: 'P2pCardEncryptionKey',
              },
            ],
            __typename: 'P2pUnauthenticatedQueryRoot',
          },
          __typename: 'UnauthenticatedQueryRoot',
        },
      })
    );
  }),

  // Pay Anyone client config
  graphql.query('PayAnyoneByTokenResponseConfig', (req, res, ctx) => {
    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            pay_anyone_client_config: {
              maintenance_mode_enabled: false,
              ocr_enabled: true,

              __typename: 'PayAnyoneClientConfig',
            },
            __typename: 'P2pUnauthenticatedQueryRoot',
          },
          __typename: 'UnauthenticatedQueryRoot',
        },
      })
    );
  }),

  // Pay Anyone by token response (main endpoint)
  graphql.query('PayAnyoneByTokenResponse', (req, res, ctx) => {
    const token = req.variables.token;

    // Reset form submission state for new token requests (except polling tokens)
    if (token !== '1' && token !== '9' && token !== '12') {
      isFormSubmitted = false;
      numberOfCalls = 0;
    }

    const baseConfig = {
      maintenance_mode_enabled: false,
      ocr_enabled: true,

      __typename: 'PayAnyoneClientConfig',
    };

    const buildGraphQLResponse = (
      status: string,
      bank = 'stride',
      gqlSpecifics: { previousClaim?: any } = {}
    ) => {
      const amount = status === 'PENDING' ? 20.7 : 10.0;
      const receiverIdentifier =
        token === '1' || token === '9'
          ? 'hatsam1@gmail.com'
          : 'pavi.krishna+thursday@chime.com';

      return {
        unauthenticated: {
          p2p: {
            pay_anyone_client_config: baseConfig,
            pay_anyone: {
              __typename: 'PayAnyoneWithExternalCard',
              pay_friend: {
                id: 'EysKMKkbN',
                ux_status: status.toUpperCase(),
                amount: amount,
                memo:
                  status === 'PENDING'
                    ? 'test again with local bank account service'
                    : ':100::fire:',
                memo_gif_id: '',
                public_id: '5a69627d-625e-4404-8033-4e6ed09f6148',
                expires_at: '2024-04-18T18:59:59Z',
                receiver_identifier: receiverIdentifier,
              },
              previous_claim: gqlSpecifics.previousClaim || null,
              sender: {
                bank: bank,
                referral_token: token === '1' ? 'hatsam1' : 'staceyylin',
                name: token === '1' ? 'Hat Sam' : 'Mickey Mouse',
                id: 'VERIx6ME',
              },
              recipient_previously_claimed: false,
              claim_with_chime: true,
            },
          },
        },
      };
    };

    const tokenWithReuseDebitValues = Number(token) >= 11111 ? '9' : token;

    switch (tokenWithReuseDebitValues) {
      case '1': {
        numberOfCalls++;
        if (isFormSubmitted) {
          const isPollingComplete = numberOfCalls > 3;
          const response = isPollingComplete
            ? buildGraphQLResponse('SUCCEEDED', 'stride')
            : buildGraphQLResponse('SENT', 'stride');
          return res(ctx.data(response));
        }
        const response = buildGraphQLResponse('PENDING', 'stride');
        return res(ctx.data(response));
      }

      case '2':
        return res(ctx.data(buildGraphQLResponse('PENDING', 'bancorp')));

      case '3':
        return res(ctx.data(buildGraphQLResponse('EXPIRED', 'bancorp')));

      case '4':
        return res(ctx.data(buildGraphQLResponse('SUCCEEDED', 'bancorp')));

      case '5':
        return res(ctx.data(buildGraphQLResponse('CANCELLED', 'bancorp')));

      case '6':
        return res(ctx.data(buildGraphQLResponse('SENT', 'bancorp')));

      case '7':
        return res(ctx.data(buildGraphQLResponse('FAILED', 'bancorp')));

      case '8':
        return res(ctx.data(buildGraphQLResponse('REVERSED', 'bancorp')));

      case '9': {
        numberOfCalls++;
        const isPolling = numberOfCalls > 1;
        return isPolling
          ? res(ctx.data(buildGraphQLResponse('SUCCEEDED', 'stride')))
          : res(
              ctx.data(
                buildGraphQLResponse('PENDING', 'stride', {
                  previousClaim: {
                    external_card_id: '5678901234',
                    first_name: 'Hat',
                    last_four: '1234',
                  },
                })
              )
            );
      }

      // Retryable Claim - Failed transaction
      case '10': {
        numberOfCalls++;
        const isPolling = numberOfCalls > 1;
        return isPolling
          ? res(ctx.data(buildGraphQLResponse('FAILED', 'stride')))
          : res(ctx.data(buildGraphQLResponse('PENDING', 'stride')));
      }

      // Retryable Claim - Reversed transaction
      case '11': {
        numberOfCalls++;
        const isPolling = numberOfCalls > 1;
        return isPolling
          ? res(ctx.data(buildGraphQLResponse('REVERSED', 'stride')))
          : res(ctx.data(buildGraphQLResponse('PENDING', 'stride')));
      }

      // Retryable Claim - Retryable transaction
      case '12': {
        numberOfCalls++;
        if (isFormSubmitted) {
          const isFirstPollingCall = numberOfCalls === 2;
          return isFirstPollingCall
            ? res(ctx.data(buildGraphQLResponse('PENDING', 'stride')))
            : res(ctx.data(buildGraphQLResponse('SUCCEEDED', 'stride')));
        }
        return res(ctx.data(buildGraphQLResponse('PENDING', 'stride')));
      }

      // Retryable Claim - Delayed success transaction
      case '13': {
        numberOfCalls++;
        const isPolling = numberOfCalls > 1;
        return isPolling
          ? res(ctx.data(buildGraphQLResponse('SENT', 'stride')))
          : res(ctx.data(buildGraphQLResponse('PENDING', 'stride')));
      }

      default:
        return res(
          ctx.status(404),
          ctx.errors([
            {
              message: 'Resource Not Found',
              extensions: { code: 404 },
            },
          ])
        );
    }
  }),

  // Pay Anyone polling endpoint
  graphql.query('PayAnyoneByTokenResponsePolling', (req, res, ctx) => {
    const { token } = req.variables;
    const mapping = pollingStatusMap[token];
    if (!mapping) {
      return res(
        ctx.errors([
          {
            message: 'Resource Not Found',
            extensions: { code: 404 },
          },
        ])
      );
    }

    // Use the same structure as the main query for consistency
    const baseConfig = {
      maintenance_mode_enabled: false,
      ocr_enabled: true,

      __typename: 'PayAnyoneClientConfig',
    };

    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            pay_anyone_client_config: baseConfig,
            pay_anyone: {
              __typename: 'PayAnyoneWithExternalCard',
              pay_friend: {
                id: 'EysKMKkbN',
                ux_status: mapping.status.toUpperCase(),
                amount: mapping.status === 'pending' ? 20.7 : 10.0,
                memo:
                  mapping.status === 'pending'
                    ? 'test again with local bank account service'
                    : ':100::fire:',
                memo_gif_id: '',
                public_id: '5a69627d-625e-4404-8033-4e6ed09f6148',
                expires_at: '2024-04-18T18:59:59Z',
                receiver_identifier: 'hatsam1@gmail.com',
              },
              previous_claim: null,
              sender: {
                bank: mapping.bank || 'stride',
                referral_token: 'hatsam1',
                name: 'Hat Sam',
                id: 'VERIx6ME',
              },
              recipient_previously_claimed: false,
              claim_with_chime: true,
            },
          },
        },
      })
    );
  }),

  // Claim Pay Anyone mutation
  graphql.mutation('ClaimPayAnyone', (req, res, ctx) => {
    const { input } = req.variables;

    const error =
      getPayAnyoneClaimError(input.token) ||
      getPayAnyoneClaimError(input.zip_code) ||
      getPayAnyoneClaimError(input.last_four);

    if (error) {
      return res(
        ctx.data({
          unauthenticated: {
            p2p: {
              claim_pay_anyone: error,
            },
          },
        })
      );
    }

    // Set form as submitted for polling simulation
    isFormSubmitted = true;

    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            claim_pay_anyone: {
              __typename: 'ClaimPayAnyoneResult',
              success: true,
              token: input.token,
            },
          },
        },
      })
    );
  }),

  // Claim Pay Anyone with saved debit card mutation
  graphql.mutation('ClaimPayAnyoneWithSavedDebitCard', (req, res, ctx) => {
    const { input } = req.variables;

    const error =
      getPayAnyoneClaimError(input.token) ||
      getPayAnyoneClaimError(input.zip_code) ||
      getPayAnyoneClaimError(input.last_four);

    if (error) {
      return res(
        ctx.data({
          unauthenticated: {
            p2p: {
              claim_pay_anyone_with_saved_debit_card: error,
            },
          },
        })
      );
    }

    // Set form as submitted for polling simulation
    isFormSubmitted = true;

    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            claim_pay_anyone_with_saved_debit_card: {
              __typename: 'ClaimPayAnyoneResult',
              success: true,
              token: input.token,
            },
          },
        },
      })
    );
  }),

  // Legacy mutation handlers for backward compatibility
  graphql.mutation('PayAnyoneClaimSavedDebitResponse', (_, res, ctx) => {
    // Set form as submitted for polling simulation
    isFormSubmitted = true;

    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            claim_pay_anyone_with_saved_debit_card: {
              __typename: 'ClaimPayAnyoneResult',
              success: true,
              token: '3N6pLxtCcpHxMsSm6CcwQBH',
            },
          },
        },
      })
    );
  }),

  graphql.mutation('PayAnyoneClaimNotSavedDebitResponse', (req, res, ctx) => {
    // Set form as submitted for polling simulation
    isFormSubmitted = true;

    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            claim_pay_anyone: {
              __typename: 'ClaimPayAnyoneResult',
              success: true,
              token: '3N6pLxtCcpHxMsSm6CcwQBH',
            },
          },
        },
      })
    );
  }),

  // Device registration
  graphql.mutation('UnauthenticatedRegisterDevice', (_, res, ctx) => {
    return res(
      ctx.data({
        unauthenticated: {
          device: {
            register_device: {
              __typename: 'RegisterDeviceResult',
              registration_id: 'registration-id',
              vendors: [],
            },
          },
        },
      })
    );
  }),

  // Device intelligence
  graphql.mutation('UnauthenticatedBuildDeviceIntelligence', (_, res, ctx) => {
    return res(
      ctx.data({
        unauthenticated: {
          device: {
            build_device_intelligence: {
              __typename: 'BuildDeviceIntelligenceResult',
              registration_id: 'registration-id',
              device_intelligence:
                'sdklkdslafjklasdfaksdfjsldkfjsldkfjsdlkfjsd',
            },
          },
        },
      })
    );
  }),

  // Step up authentication
  graphql.mutation('UnauthenticatedCreateStepUp', (_, res, ctx) => {
    return res(
      ctx.data({
        unauthenticated: {
          step_up: {
            create_step_up: {
              __typename: 'CreateStepUpResult',
              id: '12ecb733-62ab-411f-8dfe-0fe32073edf3',
              step_up_challenge_type: 'SECURE_3D',
              step_up_challenge_vendor: 'CARDINAL',
              step_up_status: 'CREATED',
            },
            __typename: 'StepUpMutation',
          },
          __typename: 'UnauthenticatedMutation',
        },
      })
    );
  }),

  graphql.mutation('UnauthenticatedInitialize3DS', (_, res, ctx) => {
    return res(
      ctx.data({
        unauthenticated: {
          step_up: {
            initialize_3ds: {
              __typename: 'StepUpInitialize3DSResult',
              id: '12ecb733-62ab-411f-8dfe-0fe32073edf3',
              pre_challenge_jwt:
                'eyJhbGciOiJIUzI1NiJ9.eyJpc3MiOiI2MDIxNjg4OTMxNjE0NjMzYzA5MDc1M2QiLCJpYXQiOjE2OTA0MDAxNDQsImV4cCI6MTY5MDQwNzM0NCwianRpIjoiYmZlNTAzOWEtOTE4Ni00NTg4LThkOTYtNjU5NGRlNTgyYWQ2IiwiQ29uc3VtZXJTZXNzaW9uSWQiOiIwXzA0Mzg0ZmU2LWNjM2MtNDFmNS04MzliLTQ3NzdlNTEyYTdmYSIsIlJlZmVyZW5jZUlkIjoiNGZjMWU1YzYtMTRhNi00ODIwLWFkOTMtMDAwY2ZmOTczNWEwIiwiYXVkIjoiMjAyMzA3MjIxODExMTcwMDE2NCIsIlBheWxvYWQiOiJ7XCJEZXZpY2VGaW5nZXJwcmludGluZ1VSTFwiOlwiaHR0cHM6Ly9nZW9zdGFnLmNhcmRpbmFsY29tbWVyY2UuY29tL0RldmljZUZpbmdlcnByaW50V2ViL1YyXCIsXCJRdWVyeVBhcmFtZXRlcnNcIjp7XCJ0aHJlYXRtZXRyaXhcIjpcInRydWVcIixcImFsaWFzXCI6XCJEZWZhdWx0XCIsXCJvcmdVbml0SWRcIjpcIjYwMWRiMmRiM2UyYmM0MTdiODYzY2EyMVwiLFwidG1FdmVudFR5cGVcIjpcIlBBWU1FTlRcIixcInJlZmVyZW5jZUlkXCI6XCI0ZmMxZTVjNi0xNGE2LTQ4MjAtYWQ5My0wMDBjZmY5NzM1YTBcIixcImdlb2xvY2F0aW9uXCI6XCJmYWxzZVwifX0sXCJIb3N0ZWRGaWVsZHNcIjpcImh0dHBzOi8vaG9zdGVkZmllbGRzc3RhZy5jYXJkaW5hbGNvbW1lcmNlLmNvbS9Ib3N0ZWRGaWVsZHNXZWIvVjFcIn0sXCJFbmFibGVkQXBwbGVQYXlcIjpmYWxzZSxcIkVuYWJsZWRDQ0FcIjp0cnVlLFwiRW5hYmxlZERpc2NvdmVyXCI6ZmFsc2UsXCJFbmFibGVkSG9zdGVkRmllbGRzXCI6ZmFsc2UsXCJFbmFibGVkUGF5cGFsXCI6ZmFsc2UsXCJFbmFibGVkVmlzYUNoZWNrb3V0XCI6ZmFsc2UsXCJGZWF0dXJlRmxhZ3NcIjp7XCJFbmFibGVkU3VwcHJlc3NlZFBheW1lbnRzVmFsaWRhdGVkUmVzcG9uc2VcIjpmYWxzZSxcIkJpblByZWxvYWRFbmFibGVkXCI6dHJ1ZX0sXCJPcHRpbWl6ZWx5RmxhZ3NcIjp7XCJTb25nYmlyZFBvc3RNZXNzYWdlVmFsaWRhdGlvblwiOmZhbHNlfSxcIkVycm9yTnVtYmVyXCI6MCxcIkVycm9yRGVzY3JpcHRpb25cIjpcIlN1Y2Nlc3NcIn0ifQ.jAlUbAzlq1iPg0nlIrL4-wovl5KtFLY_SRQrqu-axDo',
              step_up_status: 'CHALLENGE_INITIALIZED',
            },
            __typename: 'StepUpMutation',
          },
          __typename: 'UnauthenticatedMutation',
        },
      })
    );
  }),

  graphql.mutation('UnauthenticatedLookUp3DS', (_, res, ctx) => {
    return res(
      ctx.data({
        unauthenticated: {
          step_up: {
            look_up_3ds: {
              __typename: 'ChallengeNotRequired3DSResult',
              id: '6de21382-2964-49c7-b450-2b8c9996082b',
              challenge_context:
                'AQICAHilKGrvKAeV2WZnKkNUKLs-aPUY_y09CDpVZou3NEsRWQGtUq2LoaGJz4x4pp0g6F_LAAACYjCCAl4GCSqGSIb3DQEHBqCCAk8wggJLAgEAMIICRAYJKoZIhvcNAQcBMB4GCWCGSAFlAwQBLjARBAyJz1a-DwUgFtCWrwUCARCAggIVD7hf5BJTPb04_mOViWq2JoBor1jt6OcQtQ30IDHQXRSHZDf64bvAhaKF9Gak7MJvI1mAU_kjBpV2OwLH3JuAt9u-5_aghCZzVnLf2SvQIC1XZGHkSwUqLlwCPSYgKnSD6Z4_GzZVmxJePt5lH2JPwWPt6SWCksej1NMfVljQ-KYcct6Vq5tM1VALWQ3mOmYvuve_DTvdYQ1G7Djympk5CVvOkZZ8ZyTpgxRoiDO5WoUhjUGFw87MwJlM2Q0R8c80ZZrVC5CnDFsBsrBBGUTOUfrByj6IY-oZxdYLIGidtzotU-vyjNmBXpJXVMVM9-utcfBiFoBxLCk6OzRIpnvjNqP4wly2t4Y1Bf4IjNIgo11k2woQx_qOslMX3y2rB2Q3m5ZYMBCu_Ds3HaXRtqJE_UndPLpk__gBIzZRAnm0d8L-_oLcXnlD9x8x969t582c1qjD6pqAd51wsWjGKNXDI9uzjJX2z9irZaix9hTDcIAM90oCTyO9acirD3uR7dNqhaCQGub2I2opUhjfJCOg-oRuVknMOseFV5SkaJ1jWQzZBCATNWtXAv4pIkW4u1n8sASrlFmGqewNRnxu0CkJRwfz0jOXDCkFQ2jYHig4vKSTBJZlkejwzEuIOHQY_ZUbgvO7j00z40yjzxP88jsLAbzcbV_g6jy4G1AOZTd2HcDsChUwXTebB_XexnDdCHiH_Sutvzk=',
              step_up_status: 'CHALLENGE_NOT_REQUIRED',
            },
            __typename: 'StepUpMutation',
          },
          __typename: 'UnauthenticatedMutation',
        },
      })
    );
  }),

  graphql.mutation('UnauthenticatedAuthenticate3DS', (_, res, ctx) => {
    return res(
      ctx.data({
        unauthenticated: {
          step_up: {
            authenticate_3ds: {
              __typename: 'StepUpAuthenticate3DSResult',
              id: '824c496e-fe49-4829-a39c-1435ffd327fa',
              challenge_context:
                'AQICAHilKGrvKAeV2WZnKkNUKLs-aPUY_y09CDpVZou3NEsRWQEGw1_KAE0usq3N8444Sfn5AAACYjCCAl8GCSqGSIb3DQEHBqCCAlAwggJMAgEAMIICRQYJKoZIhvcNAQcBMB4GCWCGSAFlAwQBLjARBAwlYMKwOLxZzwC86U4CARCAggIW6fWeL64Kxhv5yzuIvHg3Odc4FYgPgrAGpai2kLUHz4Dsz3nZnxvJNZKHLCRJn6Kn_R1iqA-q3N_lNfeNdyU4SBMPN7rCvVQfGkC13crG3Pp5YFYAUM9RV8-He48ubOM3Ib7KH4DLlfahk8ETOYKnk80oCg621uvDcRNl8iiZ9ANS5Limi_7JLYe_Z8LzXISSDs3aXxgcYjcO8HV8XZmoQodPHt8dgwDmhzIIq-Hh1MiS15TjuQyITUIPC9TGFWDYUbF-88o6Nxh7kUY2F2wOdOSNFuCaUwFth5KzEJPw_Jqr1tWqX6-wowS95a2V4OCuaoggA7IOjnooLMHol3VG-puJjiXLtGLC_6wO-f4locNCspsI37KCQ4rsFY1eqO9shKM69rhgVqlVcsGfRXacMwLwfVYANgF6GlVaM9dxx5-nRjXbiW5BIfwO61dOuYs1fuSD8sxWq1sQiRmvmWEj1KHcYavPeq2_zdFpQ3XwkzFCGnzpA2eq6A18sQUzumRwPKoLMW4so0fDGsvipxvZwbxhhtYDdZEqLgqRhUcNs93pw2WsD2iUUgXy15EQiXdBoEBgqOFapFWzInskYYt5NxxX2Uci9RoYLu4gop7RvyYHL7cqGqe69Pb4eHgPoVpwS8iRkARZ6A3dZmkHGVg5dNyLwYWJ7g4FG8JHAQlJc27QsYV3tuCktGSHKE1ALBENwZCNWoo1',
              step_up_status: 'CHALLENGE_AUTHENTICATED',
            },
            __typename: 'StepUpMutation',
          },
          __typename: 'UnauthenticatedMutation',
        },
      })
    );
  }),

  // Cardinal Commerce endpoints
  rest.post(
    'https://centinelapistag.cardinalcommerce.com/V1/Order/JWT/Init',
    (_, res, ctx) => {
      return res(
        ctx.json({
          CardinalJWT:
            'eyJhbGciOiJIUzI1NiJ9.eyJpc3MiOiI2MDIxNjg4OTMxNjE0NjMzYzA5MDc1M2QiLCJpYXQiOjE2OTA0MDAxNDQsImV4cCI6MTY5MDQwNzM0NCwianRpIjoiYmZlNTAzOWEtOTE4Ni00NTg4LThkOTYtNjU5NGRlNTgyYWQ2IiwiQ29uc3VtZXJTZXNzaW9uSWQiOiIwXzA0Mzg0ZmU2LWNjM2MtNDFmNS04MzliLTQ3NzdlNTEyYTdmYSIsIlJlZmVyZW5jZUlkIjoiNGZjMWU1YzYtMTRhNi00ODIwLWFkOTMtMDAwY2ZmOTczNWEwIiwiYXVkIjoiMjAyMzA3MjIxODExMTcwMDE2NCIsIlBheWxvYWQiOiJ7XCJEZXZpY2VGaW5nZXJwcmludGluZ1VSTFwiOlwiaHR0cHM6Ly9nZW9zdGFnLmNhcmRpbmFsY29tbWVyY2UuY29tL0RldmljZUZpbmdlcnByaW50V2ViL1YyXCIsXCJRdWVyeVBhcmFtZXRlcnNcIjp7XCJ0aHJlYXRtZXRyaXhcIjpcInRydWVcIixcImFsaWFzXCI6XCJEZWZhdWx0XCIsXCJvcmdVbml0SWRcIjpcIjYwMWRiMmRiM2UyYmM0MTdiODYzY2EyMVwiLFwidG1FdmVudFR5cGVcIjpcIlBBWU1FTlRcIixcInJlZmVyZW5jZUlkXCI6XCI0ZmMxZTVjNi0xNGE2LTQ4MjAtYWQ5My0wMDBjZmY5NzM1YTBcIixcImdlb2xvY2F0aW9uXCI6XCJmYWxzZVwifX0sXCJIb3N0ZWRGaWVsZHNcIjpcImh0dHBzOi8vaG9zdGVkZmllbGRzc3RhZy5jYXJkaW5hbGNvbW1lcmNlLmNvbS9Ib3N0ZWRGaWVsZHNXZWIvVjFcIn0sXCJFbmFibGVkQXBwbGVQYXlcIjpmYWxzZSxcIkVuYWJsZWRDQ0FcIjp0cnVlLFwiRW5hYmxlZERpc2NvdmVyXCI6ZmFsc2UsXCJFbmFibGVkSG9zdGVkRmllbGRzXCI6ZmFsc2UsXCJFbmFibGVkUGF5cGFsXCI6ZmFsc2UsXCJFbmFibGVkVmlzYUNoZWNrb3V0XCI6ZmFsc2UsXCJGZWF0dXJlRmxhZ3NcIjp7XCJFbmFibGVkU3VwcHJlc3NlZFBheW1lbnRzVmFsaWRhdGVkUmVzcG9uc2VcIjpmYWxzZSxcIkJpblByZWxvYWRFbmFibGVkXCI6dHJ1ZX0sXCJPcHRpbWl6ZWx5RmxhZ3NcIjp7XCJTb25nYmlyZFBvc3RNZXNzYWdlVmFsaWRhdGlvblwiOmZhbHNlfSxcIkVycm9yTnVtYmVyXCI6MCxcIkVycm9yRGVzY3JpcHRpb25cIjpcIlN1Y2Nlc3NcIn0ifQ.jAlUbAzlq1iPg0nlIrL4-wovl5KtFLY_SRQrqu-axDo',
        })
      );
    }
  ),

  rest.post(
    'https://centinelapistag.cardinalcommerce.com/V1/Order/JWT/Continue',
    (_, res, ctx) => {
      return res(
        ctx.json({
          CardinalJWT:
            'eyJhbGciOiJIUzI1NiJ9.eyJpc3MiOiI2MDIxNjg4OTMxNjE0NjMzYzA5MDc1M2QiLCJpYXQiOjE2OTA0MDgyNzcsImV4cCI6MTY5MDQxNTQ3NywianRpIjoiYTRjZTBhNTMtYmRiZC00ZDY3LTg0ZGEtNzZhNDQwNDU1OGZlIiwiQ29uc3VtZXJTZXNzaW9uSWQiOiIwXzA0Mzg0ZmU2LWNjM2MtNDFmNS04MzliLTQ3NzdlNTEyYTdmYSIsIlJlZmVyZW5jZUlkIjoiNDdjZjZjOTItMTgxMy00YjE1LWJiYmYtOWU0NDE4ZGUzZmUxIiwiYXVkIjoiMjAyMzA3MjIxODExMTcwMDE2NyIsIlBheWxvYWQiOiJ7XCJFcnJvck51bWJlclwiOjAsXCJFcnJvckRlc2NyaXB0aW9uXCI6XCJTdWNjZXNzXCJ9In0.93JyNeN1I3UKkYdx_sZ595quzqzvNIkM0LqWDEszlfw',
        })
      );
    }
  ),

  rest.post('https://writer.cardinalcommerce.com/stag/log', (_, res, ctx) => {
    return res(ctx.json({ status: 'ok' }));
  }),

  // Pay Anyone referral token query
  graphql.query('UsePayAnyoneIncentiveReferralTokenQuery', (_, res, ctx) => {
    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            pay_anyone_referral: {
              __typename: 'P2pPayAnyoneReferral',
              amount: 15.0,
              expires_at: '2029-03-19T03:37:25Z',
              token: '1',
              ready_to_claim: false,
              sender_name: 'Sender Name',
              sender_referral_token: 'senderReferralToken',
            },
            __typename: 'P2pUnauthenticatedQueryRoot',
          },
          __typename: 'UnauthenticatedQueryRoot',
        },
      })
    );
  }),
];
